<script>
import { mapActions, mapGetters } from 'vuex'
import Action from '@/components/Action'
import Icon from '@/components/Icon'
import RadioGroup from '@/components/RadioGroup'
import RadioField from '@/components/RadioField'
import debounce from 'lodash/debounce'
import logger from '@/logger/index'

export default {
  name: 'FilterSearch',

  components: {
    Action,
    Icon,
    RadioGroup,
    RadioField,
  },

  props: {
    filters: {
      type: Array,
      default: () => [],
    },

    defaultValue: {
      type: String,
      default: null,
    },

    placeholder: {
      type: String,
      default: null,
    },

    results: {
      type: Array,
      default: () => [],
    },

    loading: {
      type: Boolean,
      default: false,
    },

    disable: {
      type: Boolean,
      default: false,
    },

    outlined: {
      type: Boolean,
      default: false,
    },

    productNotFound: {
      type: Boolean,
      default: false,
    },

    currentFilter: {
      type: String,
      default: null,
    },
  },

  data () {
    return {
      filterSearch: null,
      search: null,
      view: 'block',
      showResults: false,
      industryFilter: 'todos', // 'todos', 'original', 'industria'
      /** Altura máxima do dropdown de resultados (px), calculada dinamicamente para preencher a viewport. */
      resultsMaxHeight: 0,
    }
  },

  computed: {
    ...mapGetters('products', ['getSearchResultsScrollPosition', 'getLastSearchTerm', 'getLastSearchTermRaw']),

    showIndustryChips () {
      return this.filterSearch === 'descricao' && this.results && this.results.length > 0
    },

    filteredResults () {
      if (!this.showIndustryChips || this.industryFilter === 'todos') {
        return this.results
      }

      return this.results.filter(product => {
        const factory = this.normalizeText(product.factory || product.CODIGO_INDUSTRIA)
        const original = this.normalizeText(product.original || product.ORIGINAIS)

        if (this.industryFilter === 'original') {
          return this.matchesSearchText(original)
        } else if (this.industryFilter === 'industria') {
          return this.matchesSearchText(factory)
        }

        return true
      })
    },

    resultsCount () {
      return this.filteredResults.length
    },
  },

  watch: {
    $media () {
      if (this.$media.mobile) {
        this.handleView('block')
      }
    },
    filterSearch (newVal) {
      // Reseta o chip de indústria sempre que troca o tipo de busca.
      this.industryFilter = 'todos'

      // Voltou pra Descrição e há resultados em cache → restaura termo (cru) + lista,
      // em vez de limpar. Resolve o vai-e-volta descrição↔código e a troca de aba
      // (no remount o mounted seta filterSearch=defaultValue, caindo aqui).
      if (newVal === 'descricao' && this.getLastSearchTermRaw && this.results && this.results.length > 0) {
        this.search = this.getLastSearchTermRaw
        this.showResults = true
        this.restoreScrollPosition()
        return
      }

      // Caso contrário (troca pra Código, ou Descrição sem cache): limpa.
      this.search = ''
      this.$emit('search', '')
      this.showResults = false
    },
    results () {
      // Mostra a lista automaticamente quando há resultados
      if (this.results && this.results.length > 0) {
        this.showResults = true
        // Restaura a posição do scroll quando os resultados são carregados
        this.restoreScrollPosition()
      }
      // Limpa o cache das classes quando os resultados mudam
      this.clearClassesCache()
    },

    showResults (val) {
      // Recalcula a altura disponível quando o dropdown aparece (o $ref só existe após render).
      if (val) this.updateResultsMaxHeight()
    },

    productNotFound (newValue) {
      if (newValue === true) {
        this.showResults = false
      }
    },

    showIndustryChips: {
      handler (isVisible) {
        if (isVisible) {
          this.$nextTick(() => {
            this.showIndustryChipsTooltip()
          })
        }
      },
    },
  },

  mounted () {
    // Restaura o MODO real (currentFilter do pai), não sempre o defaultValue.
    // Na troca de aba o Tab usa v-if e remonta este componente; o data local
    // (filterSearch) é recriado, mas o `filter` do pai persiste. Setar
    // defaultValue forçava o radio de volta pra "descricao" enquanto o pai
    // continuava em "codigo" — radio e canShowFilters ficavam dessincronizados
    // (botão/painel de filtro não apareciam). Usar currentFilter mantém coerência.
    // Setar filterSearch dispara o watcher `filterSearch`, que restaura termo +
    // lista do cache quando o modo é descrição.
    this.filterSearch = this.currentFilter || this.defaultValue
    window.addEventListener('keydown', this.checkEscape)
    window.addEventListener('resize', this.debouncedResize)

    this.updateResultsMaxHeight()
    this.observeResultsBounds()
  },

  created () {
    // Recalcula altura disponível ao redimensionar a janela (debounced).
    this.debouncedResize = debounce(() => this.updateResultsMaxHeight(), 100)

    // Cria uma função debounce para a busca
    // que será chamada quando o usuário digitar no campo de pesquisa
    this.debouncedSearch = debounce(() => {
      // Verifica se já está carregando
      if (this.loading) {
        return
      }

      // Reusa lista cacheada quando o termo bate com o último search de descricao.
      if (this.tryShowDescricaoCache()) {
        logger.info('Mostrar os resultados do cache')
        return
      }

      // Só busca se tiver 3+ caracteres (lastSearchTerm no Vuex é atualizado em searchProducts quando type === 'descricao')
      const trimmedSearch = this.search ? this.search.trim() : ''
      if (trimmedSearch.length >= 3) {
        this.handleSearch()
      }
      // Se tiver 1-2 caracteres, não faz nada (aguarda mais)
    }, 1000)
  },

  beforeDestroy () {
    // limpa o listener de escape e cancela pendências de debounce
    window.removeEventListener('keydown', this.checkEscape)
    window.removeEventListener('resize', this.debouncedResize)
    this.debouncedSearch.cancel()
    this.unobserveResultsBounds()
    if (this.debouncedResize && this.debouncedResize.cancel) {
      this.debouncedResize.cancel()
    }
  },
  methods: {
    ...mapActions('products', ['setSearchResultsScrollPosition']),
    ...mapActions('userTips', ['showTip']),

    /**
     * Observa o [data-results-bounds] (desktop) com ResizeObserver.
     * Necessário porque o .main-content cresce conforme o filtro é populado
     * com opções — sem isso o cálculo inicial fica curto (ex.: 402px).
     */
    observeResultsBounds () {
      if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return
      const bounds = document.querySelector('[data-results-bounds]')
      if (!bounds) return
      this.resultsBoundsRO = new ResizeObserver(() => this.updateResultsMaxHeight())
      this.resultsBoundsRO.observe(bounds)
    },

    unobserveResultsBounds () {
      if (this.resultsBoundsRO) {
        this.resultsBoundsRO.disconnect()
        this.resultsBoundsRO = null
      }
    },

    /**
     * Calcula a altura máxima do dropdown de resultados.
     * - Desktop: tenta usar [data-results-bounds] (no Products.vue é o
     *   .main-content) como limite — assim termina alinhado com esse container.
     * - Mobile/tablet (ou desktop sem o atributo): cai pra viewport.
     */
    updateResultsMaxHeight () {
      this.$nextTick(() => {
        const el = this.$refs.resultsContainer
        if (!el || typeof window === 'undefined') return
        const rect = el.getBoundingClientRect()
        const bottomGap = 24 // respiro no fim do limite

        // No desktop, tenta encaixar dentro do [data-results-bounds].
        let bottomLimit = window.innerHeight
        if (this.$media && this.$media.desktop) {
          const bounds = document.querySelector('[data-results-bounds]')
          if (bounds) {
            const boundsBottom = bounds.getBoundingClientRect().bottom
            if (boundsBottom > rect.top) bottomLimit = boundsBottom
          }
        }

        const available = bottomLimit - rect.top - bottomGap
        // Mínimo de 240px pra não ficar minúsculo em telas baixas.
        this.resultsMaxHeight = Math.max(240, Math.floor(available))
      })
    },

    computedClasses (product) {
      if (product._classesCache) {
        return product._classesCache
      }

      const successClass = this.getSuccessClass(product)
      const classes = {
        [this.$classes['success-low']]: successClass === 'success-low',
        [this.$classes['success-medium']]: successClass === 'success-medium',
        [this.$classes['success-high']]: successClass === 'success-high',
      }

      // Armazena cache no próprio objeto
      product._classesCache = classes
      return classes
    },

    getSuccessClass (item) {
      const percentual = item.successPercentage

      if (typeof percentual !== 'number' || isNaN(percentual)) {
        return ''
      }

      if (percentual < 0.5) return 'success-low'
      if (percentual < 1) return 'success-medium'
      if (percentual === 1) return 'success-high'
      return ''
    },

    clearClassesCache () {
      if (this.results && Array.isArray(this.results)) {
        this.results.forEach(product => {
          delete product._classesCache
        })
      }
    },

    handleView (viewName) {
      this.view = viewName
      this.$emit('view', this.view)
    },

    handleSearch () {
      this.$emit('search', this.search)
    },

    onSearchInput () {
      // Código Scherer é exclusivamente numérico: remove qualquer caractere não
      // dígito já na digitação (e no colar). Impede a busca por um valor que o
      // backend truncaria (cast (int) pega só o prefixo e devolve produto errado).
      if (this.filterSearch === 'codigo' && this.search) {
        const onlyDigits = this.search.replace(/\D/g, '')
        if (onlyDigits !== this.search) this.search = onlyDigits
      }
      this.debouncedSearch()
    },

    handleFilter () {
      this.$emit('filter', this.filterSearch)
    },

    selectProduct (product) {
      this.$emit('select', product)
    },

    onProductCardClick (product, event) {
      this.selectProduct(product)
      if (event && event.currentTarget) {
        event.currentTarget.blur()
      }
    },

    onSubmit (event) {
      // Segurar Enter dispara keydown em autorepeat (~30/s) e cada repetição
      // caía aqui. `event.repeat` distingue a rajada do teclado de um Enter
      // novo do usuário. Só existe no keydown — no clique do botão e no submit
      // do form vem undefined.
      if (event && event.repeat) return

      if (this.loading) return

      // Cancela qualquer debounce pendente e busca imediatamente
      this.debouncedSearch.cancel()

      if (!this.tryShowDescricaoCache()) {
        this.handleSearch()
      }

      // Tira o foco do botão para que Enter não reative a busca depois do clique.
      // Não blura o input — usuário deve poder continuar digitando após o Enter.
      const target = event && event.currentTarget
      if (target && target.tagName === 'BUTTON' && typeof target.blur === 'function') {
        target.blur()
      }
    },

    handleScroll (event) {
      this.setSearchResultsScrollPosition(event.target.scrollTop)
    },

    restoreScrollPosition () {
      this.$nextTick(() => {
        const resultsContainer = this.$refs.resultsContainer
        if (resultsContainer && this.getSearchResultsScrollPosition > 0) {
          resultsContainer.scrollTop = this.getSearchResultsScrollPosition
        }
      })
    },

    clearSearch () {
      this.search = ''
      this.$emit('search', '')
    },

    showIndustryChipsTooltip () {
      this.$nextTick(() => {
        if (this.$refs.industryChips) {
          this.showTip({
            key: 'industryChipsFilter',
            title: 'Novo filtro de Original e Indústria',
            content: 'A busca mantém a mesma! Mas agora você consegue filtrar Original e Indústria e visualizar separadamente se procurar algum produto específico. \n \n • Funciona apenas quando você digita o código exato do produto.',
            targetRef: this.$refs.industryChips,
            position: 'top',
            delay: 600,
            startDate: '18/11/2025',
            validForMonths: 3,
          })
        }
      })
    },

    normalizeText (value) {
      if (value === null || value === undefined) {
        return ''
      }

      if (Array.isArray(value)) {
        return value.map(item => this.normalizeText(item)).join(' ')
      }

      if (typeof value === 'object') {
        return Object.values(value)
          .map(item => this.normalizeText(item))
          .join(' ')
      }

      return String(value)
    },

    matchesSearchText (text) {
      const searchText = this.search ? this.search.trim().toUpperCase() : ''
      const normalizedText = this.normalizeText(text).trim().toUpperCase()

      if (!searchText || !normalizedText) {
        return false
      }

      if (normalizedText === searchText) {
        return true
      }

      const searchWords = searchText.split(/\s+/).filter(Boolean)
      const textWords = normalizedText.split(/\s+/).filter(Boolean)

      if (!searchWords.length || !textWords.length) {
        return false
      }

      return searchWords.every(word => textWords.includes(word))
    },

    handleIndustryFilter (filter) {
      this.industryFilter = filter
    },

    tryShowDescricaoCache () {
      if (this.filterSearch !== 'descricao') return false

      const normalizedSearch = (this.search || '').trim().toLowerCase()
      if (!normalizedSearch || normalizedSearch !== this.getLastSearchTerm) return false

      if (!this.results || this.results.length === 0) return false

      this.showResults = true
      this.restoreScrollPosition()
      return true
    },

  },

  detroyed () {
    window.removeEventListener('keydown', this.checkEscape)
  },

}
</script>

<template>
  <div :class="[$classes['filter'], { [$classes['disabled']]: disable }]">
    <div :class="$classes['filter-type-order']">
      <slot name="before-filter-type" />
      <div :class="$classes['filter-type']">
        <radio-group v-if="filters.length" variant="segmented">
          <template v-for="(filter, index) in filters">
            <radio-field :key="index" is-group variant="segmented" :icon="filter.icon" :label="filter.label"
              :value="filter.value" v-model="filterSearch" @change="handleFilter" :disabled="disable" />
          </template>
        </radio-group>
      </div>
    </div>
    <form @submit.prevent="onSubmit" :class="[$classes['filter-search'], { [$classes['outlined']]: outlined }]">
      <div :class="$classes['filter-search-input-wrapper']">
        <!-- Enter dispara onSubmit. O spam é contido em três camadas: `event.repeat`
             (autorepeat do teclado), o `loading` (request em voo) e a trava por chave
             do componente pai (searchGuard), que também cobre a busca sem resultado. -->
        <input required="true" oninvalid="this.setCustomValidity('Preencha o campo de pesquisa')"
          oninput="setCustomValidity('')" spellcheck="false" type="text" :placeholder="placeholder" v-model="search"
          @input="onSearchInput" @keydown.enter.prevent="onSubmit" :class="$classes['filter-search-input']" :disabled="disable">
        <action v-if="search && search.length > 0" type="button" :class="$classes['clear-btn']"
          @click.prevent="clearSearch" :disabled="disable">
          <icon name="times" :class="$classes['clear-btn-icon']" />
        </action>
        <action type="button" :class="$classes['search-btn']" @click.prevent="onSubmit" :loading="loading"
          :disabled="disable || loading">
          <icon v-if="!loading" name="search" :class="$classes['search-btn-icon']" />
        </action>
      </div>
      <slot name="before-results" />
      <div :class="$classes['filter-search-results']" v-if="results.length && showResults && !productNotFound"
        :style="resultsMaxHeight ? { maxHeight: resultsMaxHeight + 'px' } : null" ref="resultsContainer"
        @scroll="handleScroll">
        <div :class="$classes['results-header']">
          <span :class="$classes['results-count']">📦 {{ resultsCount }} produtos encontrados</span>
          <div v-if="showIndustryChips" ref="industryChips" :class="$classes['industry-chips']">
            <action type="button"
              :class="[$classes['industry-chip'], { [$classes['industry-chip-active']]: industryFilter === 'todos' }]"
              @click="handleIndustryFilter('todos')">
              Todos
            </action>
            <action type="button"
              :class="[$classes['industry-chip'], { [$classes['industry-chip-active']]: industryFilter === 'original' }]"
              @click="handleIndustryFilter('original')">
              Original
            </action>
            <action type="button"
              :class="[$classes['industry-chip'], { [$classes['industry-chip-active']]: industryFilter === 'industria' }]"
              @click="handleIndustryFilter('industria')">
              Indústria
            </action>
          </div>
        </div>
        <div :class="$classes['filter-search-results-list']">
          <div v-if="filteredResults.length === 0 && showIndustryChips" :class="$classes['no-filtered-results']">
            Nenhum produto encontrado com o filtro selecionado.
          </div>
          <template v-for="(product, index) in filteredResults">
            <div :key="index" :class="[$classes['product-card'], computedClasses(product)]">
              <action :class="$classes['product-card-btn']" @click="onProductCardClick(product, $event)">
                <!-- Seção Principal (Esquerda) -->
                <div :class="$classes['product-main-info']">
                  <div :class="$classes['product-code']">Código: {{ product.id || product.CODIGO }}</div>
                  <div :class="$classes['product-description']">{{ product.description || product.DESCRICAO_CURTA }}
                    <div :class="$classes['product-brand']">{{ product.factory || product.MARCA_NOME }}</div>
                  </div>
                  <!-- Caixa de MSG Consulta (Inferior) -->
                  <!-- <div :class="$classes['product-message-box']">
                    <div :class="$classes['message-content']">ℹ️ teste teste teste teste teste teste teste teste teste
                    </div>
                  </div> -->
                </div>

                <!-- Caixa de Aplicação (Direita) -->
                <div v-if="product.fullApplication || product.APLICACAO_COMPLETA"
                  :class="$classes['product-application-box']">
                  <div :class="$classes['application-label']">Aplicação</div>
                  <div :class="$classes['application-content']">
                    {{ product.fullApplication || product.APLICACAO_COMPLETA }}
                  </div>
                </div>

              </action>
            </div>
          </template>
        </div>
      </div>
    </form>
  </div>
</template>

<style module>
.filter {
  position: relative;
  width: 100%;
}

.filter-search.outlined {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.filter-search.outlined:hover {
  border-color: #bdbdbd;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.filter-search.outlined .filter-search-input {
  border: 1px solid #e0e0e0;
  border-right: none;
  border-radius: 4px 0 0 4px;
  background: #ffffff;
  padding: 8px 12px;
  height: 40px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.filter-search.outlined .filter-search-input:hover {
  border-color: #bdbdbd;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.filter-search.outlined .filter-search-input:focus {
  border-color: #4A94D4;
  box-shadow: 0 0 0 3px rgba(74, 148, 212, 0.1);
  outline: none;
}

.filter-search.outlined .search-btn {
  border-radius: 0 4px 4px 0;
  height: 40px;
}

.filter:has(.filter-search-input:disabled) {
  opacity: 0.8;
  cursor: not-allowed;
}

/* Fallback para navegadores que não suportam :has() */
.filter.disabled {
  opacity: 0.8;
  position: relative;
  cursor: not-allowed;
}

.filter.disabled::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 4px;
  z-index: 1;
  pointer-events: none;
}

.filter.outlined.disabled {
  border-color: #dee2e6;
  background: #f8f9fa;
  opacity: 0.8;
  cursor: not-allowed;
}

.filter.outlined.disabled .filter-search-input {
  border-color: #dee2e6;
  background: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  opacity: 0.8;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.08);
}

.filter-type-order {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.filter-type {
  flex: 1;
  min-width: 0;
}

.filter-type :global(.form-radio-item.is-disabled) {
  opacity: 0.6;
}

.filter-type :global(.form-radio-item.is-disabled .form-radio-label) {
  color: #6c757d;
  cursor: not-allowed;
}

.filter-type :global(.form-radio-item.is-disabled .form-radio-icon) {
  opacity: 0.5;
  filter: grayscale(0.3);
}

.filter-view {
  display: flex;
  align-items: center;
}

.filter-view-action {
  width: 24px;
  height: 24px;
  color: #DBDBDB;
  margin-right: 10px;
}

.filter-view-action.is-active {
  color: #5CC0DE;
}

.filter-view-action-icon {
  width: 16px;
  height: 16px;
}

.filter-search {
  position: relative;
}

.filter-search-input-wrapper {
  display: flex;
}

.filter-search-input {
  border: 1px solid #e0e0e0;
  border-right: none;
  border-radius: 4px 0 0 4px;
  display: block;
  width: 100%;
  flex: 1;
  background: #fff;
  color: #292929;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, Cantarell, "Helvetica Neue", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
  font-size: 16px;
  padding-left: 30px;
  padding-right: 10px;
  transition: all 0.2s ease-in-out;
}

.filter-search-input:disabled {
  background: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  opacity: 0.8;
  border: 2px solid #dee2e6;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.08);
  position: relative;
}

.filter-search-input:disabled::placeholder {
  color: #adb5bd;
  font-style: italic;
}

.search-btn {
  background: #D14040;
  color: #FFF;
  width: 50px;
  height: 40px;
  border-radius: 0 4px 4px 0;
  transition: all 0.2s ease-in-out;
}

.search-btn:hover:not(:disabled) {
  background: #B83333;
}

.search-btn:disabled {
  background: #adb5bd;
  color: #f8f9fa;
  cursor: not-allowed;
  opacity: 0.8;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.15);
  border: 2px solid #ced4da;
}

.search-btn-icon {
  width: 20px;
  height: 20px;
}

.clear-btn {
  border: 1px solid #e0e0e0;
  border-right: none;
  border-left: none;
  background: #FFF;
  color: #A2A2A2;
  width: 40px;
  height: 40px;
  border-radius: 0;
  transition: all 0.2s ease-in-out;
  margin-right: 2px;
}

.clear-btn:hover {
  border: 1px solid #e0e0e0;
  border-right: none;
  border-left: none;
  background: #f8f9fa;
  color: #6c757d;
}

.clear-btn:disabled {
  background: #adb5bd;
  color: #f8f9fa;
  cursor: not-allowed;
  opacity: 0.8;
}

.clear-btn-icon {
  width: 16px;
  height: 16px;
}

.filter-search-results {
  position: absolute;
  top: 100%;
  left: -16px;
  right: -16px;
  margin-top: 20px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  /* Fallback CSS — o valor real é calculado em JS via :style inline
     (window.innerHeight - rect.top - 24px) e aplicado em updateResultsMaxHeight(). */
  max-height: calc(100vh - 280px);
  overflow-x: hidden;
  overflow-y: scroll;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  scrollbar-width: thin;
  scrollbar-color: #a2a2a2 transparent;
}

.filter-search-results::-webkit-scrollbar {
  width: 6px;
}

.filter-search-results::-webkit-scrollbar-track {
  background-color: transparent;
}

.filter-search-results::-webkit-scrollbar-thumb {
  background-color: #C7C7C7;
  border-radius: 8px;
}

.filter-search-results::-webkit-scrollbar-thumb:hover {
  background-color: #A1A1A1;
}

.results-header {
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #f8f9fa;
  border-radius: 12px 12px 0 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.results-count {
  font-size: 14px;
  font-weight: 600;
  color: #3D3D3D;
}

.industry-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.industry-chip {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  background: #fff;
  color: #5C5C5C;
  border: 1px solid #E2E2E2;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

.industry-chip:hover {
  background: #F8F8F9;
  border-color: #C7C7C7;
  color: #3D3D3D;
}

.industry-chip-active {
  background: #3D3D3D;
  color: #fff;
  border-color: #3D3D3D;
}

.industry-chip-active:hover {
  background: #292929;
  border-color: #292929;
  color: #fff;
}

.filter-search-results-list {
  padding: 8px;
}

.no-filtered-results {
  padding: 20px;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
}

.product-card {
  margin-bottom: 12px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
}

.product-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
  border-color: #C7C7C7;
}

.product-card-btn {
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background: #fff;
  color: #2c3e50;
  text-align: left;
  border: none;
  cursor: pointer;
}

.product-main-info {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-brand {
  font-size: 12px;
  font-weight: 700;
  color: #5C5C5C;
  text-transform: uppercase;
}

.product-description {
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
}

.product-code {
  font-size: 14px;
  color: #6c757d;
  font-family: monospace;
}

.product-application-box {
  background: #f8f9fa;
  margin: 5px 16px;
  padding: 16px;
  border-radius: 8px;
  width: 40%;
  border: 1px solid #e9ecef;
}

.application-label {
  font-size: 12px;
  font-weight: 600;
  color: #5C5C5C;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.application-content {
  font-size: 14px;
  color: #495057;
  line-height: 1.5;
  white-space: pre-line;
}

.product-message-box {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  margin: 0 20px 20px;
  padding: 12px 16px;
  border-radius: 8px;
}

.message-content {
  font-size: 13px;
  color: #856404;
  line-height: 1.4;
}

.success-low {
  border-left: 4px solid #dc3545;
}

.success-medium {
  border-left: 4px solid #ffc107;
}

.success-high {
  border-left: 4px solid #28a745;
}

/* Hover effects para as classes de sucesso */
.success-low:hover {
  background-color: rgba(220, 53, 69, 0.1) !important;
  border-left-color: #c82333;
}

.success-medium:hover {
  background-color: rgba(255, 193, 7, 0.1) !important;
  border-left-color: #e0a800;
}

.success-high:hover {
  background-color: rgba(40, 167, 69, 0.1) !important;
  border-left-color: #1e7e34;
}

/* Estilos responsivos para mobile */
@media (max-width: 768px) {
  .filter-search-results {
    margin-top: 4px;
  }

  .results-header {
    padding: 12px 16px 8px;
  }

  .results-count {
    font-size: 13px;
  }

  .filter-search-results-list {
    padding: 4px;
  }

  .product-card {
    margin-bottom: 8px;
  }

  .product-main-info {
    padding: 16px;
    gap: 6px;
  }

  .product-brand {
    font-size: 14px;
  }

  .product-description {
    font-size: 13px;
  }

  .product-code {
    font-size: 12px;
  }

  .product-application-box {
    margin: 0 16px 12px;
    padding: 12px;
  }

  .application-label {
    font-size: 11px;
    margin-bottom: 6px;
  }

  .application-content {
    font-size: 12px;
  }

  .product-message-box {
    margin: 0 16px 16px;
    padding: 10px 12px;
  }

  .message-content {
    font-size: 12px;
  }
}

@media (max-width: 767px) {

  /* TABLET e MOBILE */
  .filter-type-order {
    display: block;
  }

  .filter-type {
    min-width: auto;
    margin-right: 0;
  }

  .filter-view {
    margin-top: 20px;
  }
}
</style>
